/**
 * Assign Material Modal
 *
 * Modal for assigning a learning material to one or more operators
 * with an optional due date.
 */

import { useState } from 'react';
import { X, Users, Calendar, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { useOperatorLookup } from '../../../hooks/useOperators';
import { useCreateAssignments } from '../../../hooks/useLearning';

interface AssignMaterialModalProps {
  materialId: string | null;
  materialTitle: string;
  isOpen: boolean;
  onClose: () => void;
}

export function AssignMaterialModal({
  materialId,
  materialTitle,
  isOpen,
  onClose,
}: AssignMaterialModalProps) {
  const [selectedOperators, setSelectedOperators] = useState<string[]>([]);
  const [dueDate, setDueDate] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [result, setResult] = useState<{ created: number; skipped: number } | null>(null);

  const { data: operators, isLoading: loadingOperators } = useOperatorLookup();
  const createAssignments = useCreateAssignments();

  const handleClose = () => {
    setSelectedOperators([]);
    setDueDate('');
    setSearchQuery('');
    setShowSuccess(false);
    setResult(null);
    onClose();
  };

  const handleToggleOperator = (operatorId: string) => {
    setSelectedOperators((prev) =>
      prev.includes(operatorId)
        ? prev.filter((id) => id !== operatorId)
        : [...prev, operatorId]
    );
  };

  const handleSelectAll = () => {
    if (!operators) return;
    const filteredIds = filteredOperators.map((op) => op.id);
    const allSelected = filteredIds.every((id) => selectedOperators.includes(id));

    if (allSelected) {
      setSelectedOperators((prev) => prev.filter((id) => !filteredIds.includes(id)));
    } else {
      setSelectedOperators((prev) => [...new Set([...prev, ...filteredIds])]);
    }
  };

  const handleAssign = async () => {
    if (!materialId || selectedOperators.length === 0) return;

    try {
      const res = await createAssignments.mutateAsync({
        materialId,
        operatorIds: selectedOperators,
        dueDate: dueDate ? new Date(dueDate) : undefined,
      });
      setResult({ created: res.created, skipped: res.skipped });
      setShowSuccess(true);
    } catch (error) {
      console.error('Assignment failed:', error);
    }
  };

  // Filter operators by search
  const filteredOperators = operators?.filter((op) => {
    if (!searchQuery) return true;
    const fullName = op.fullName.toLowerCase();
    const employeeId = op.employeeId.toLowerCase();
    const query = searchQuery.toLowerCase();
    return fullName.includes(query) || employeeId.includes(query);
  }) ?? [];

  // Get min date (today)
  const today = new Date().toISOString().split('T')[0];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="relative w-full max-w-lg rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">
            {showSuccess ? 'Assignment Complete' : 'Assign Material'}
          </h2>
          <button
            onClick={handleClose}
            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {showSuccess ? (
            <div className="flex flex-col items-center py-6">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
                <CheckCircle2 size={32} className="text-emerald-600" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-gray-900">
                Successfully Assigned!
              </h3>
              <p className="text-center text-sm text-gray-500">
                {result?.created} operator{result?.created !== 1 ? 's' : ''} assigned
                {result?.skipped ? ` (${result.skipped} already had this material)` : ''}
              </p>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Material Info */}
              <div className="rounded-xl bg-gray-50 p-4">
                <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  Assigning Material
                </div>
                <div className="mt-1 font-semibold text-gray-900">{materialTitle}</div>
              </div>

              {/* Operator Selection */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">
                  Select Operators <span className="text-red-500">*</span>
                </label>

                {/* Search */}
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search operators..."
                  className="mb-3 w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm transition-colors focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20"
                />

                {/* Operator List */}
                <div className="max-h-56 overflow-y-auto rounded-xl border border-gray-200">
                  {loadingOperators ? (
                    <div className="flex h-32 items-center justify-center">
                      <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                    </div>
                  ) : filteredOperators.length === 0 ? (
                    <div className="flex h-32 items-center justify-center text-sm text-gray-500">
                      No operators found
                    </div>
                  ) : (
                    <>
                      {/* Select All */}
                      <button
                        onClick={handleSelectAll}
                        className="flex w-full items-center gap-3 border-b border-gray-100 px-4 py-3 text-left transition-colors hover:bg-gray-50"
                      >
                        <div
                          className={`flex h-5 w-5 items-center justify-center rounded border-2 transition-colors ${
                            filteredOperators.every((op) =>
                              selectedOperators.includes(op.id)
                            )
                              ? 'border-guild-green bg-guild-green text-white'
                              : 'border-gray-300'
                          }`}
                        >
                          {filteredOperators.every((op) =>
                            selectedOperators.includes(op.id)
                          ) && (
                            <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                        </div>
                        <span className="text-sm font-medium text-gray-700">
                          Select All ({filteredOperators.length})
                        </span>
                      </button>

                      {/* Operator Items */}
                      {filteredOperators.map((operator) => (
                        <button
                          key={operator.id}
                          onClick={() => handleToggleOperator(operator.id)}
                          className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50"
                        >
                          <div
                            className={`flex h-5 w-5 items-center justify-center rounded border-2 transition-colors ${
                              selectedOperators.includes(operator.id)
                                ? 'border-guild-green bg-guild-green text-white'
                                : 'border-gray-300'
                            }`}
                          >
                            {selectedOperators.includes(operator.id) && (
                              <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            )}
                          </div>
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-guild-green to-guild-green/80 text-xs font-bold text-white">
                            {operator.fullName.split(' ').map(n => n[0]).join('').slice(0, 2)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="truncate text-sm font-medium text-gray-900">
                              {operator.fullName}
                            </div>
                            <div className="truncate text-xs text-gray-500">
                              {operator.employeeId}
                            </div>
                          </div>
                        </button>
                      ))}
                    </>
                  )}
                </div>

                {selectedOperators.length > 0 && (
                  <div className="mt-2 flex items-center gap-1.5 text-sm text-guild-green">
                    <Users size={14} />
                    <span>{selectedOperators.length} operator{selectedOperators.length !== 1 ? 's' : ''} selected</span>
                  </div>
                )}
              </div>

              {/* Due Date */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">
                  Due Date (optional)
                </label>
                <div className="relative">
                  <Calendar
                    size={16}
                    className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    min={today}
                    className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-11 pr-4 text-sm transition-colors focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20"
                  />
                </div>
              </div>

              {createAssignments.isError && (
                <div className="flex items-center gap-2 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
                  <AlertCircle size={16} />
                  Failed to assign material. Please try again.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t border-gray-100 px-6 py-4">
          {showSuccess ? (
            <button
              onClick={handleClose}
              className="rounded-xl bg-guild-green px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-guild-green/90"
            >
              Done
            </button>
          ) : (
            <>
              <button
                onClick={handleClose}
                className="rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAssign}
                disabled={selectedOperators.length === 0 || createAssignments.isPending}
                className="flex items-center gap-2 rounded-xl bg-guild-green px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-guild-green/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {createAssignments.isPending ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Assigning...
                  </>
                ) : (
                  <>
                    <Users size={16} />
                    Assign to {selectedOperators.length} Operator{selectedOperators.length !== 1 ? 's' : ''}
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
